import React, { useState, useEffect } from 'react';
import { Container, Grid, Card, Text, Title, Group, Stack, Button, Badge, Image, SimpleGrid, Skeleton, TextInput, NumberInput, Pagination, Paper } from '@mantine/core';
import { IconSearch, IconMapPin, IconTent, IconPhone, IconFilter, IconX } from '@tabler/icons-react';
import { Link } from 'react-router-dom';

const Campgrounds = () => {
  const [campgrounds, setCampgrounds] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAmenities, setLoadingAmenities] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zipcode, setZipcode] = useState('');
  const [radius, setRadius] = useState(50); // Default 50 miles
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    state: '',
    city: ''
  });

  useEffect(() => {
    fetchAmenities();
    fetchCampgrounds();
  }, [currentPage, filters, selectedAmenities]);

  const fetchAmenities = async () => {
    try {
      const response = await fetch('/api/amenities');
      const data = await response.json();
      if (data.success) {
        setAmenities(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching amenities:', error);
    } finally {
      setLoadingAmenities(false);
    }
  };

  const fetchCampgrounds = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: 12,
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== '')),
        ...(selectedAmenities.length > 0 && { amenityIds: selectedAmenities.join(',') })
      });

      // Add zipcode and radius if provided
      if (zipcode) {
        queryParams.append('zipcode', zipcode);
        queryParams.append('radius', radius);
      }

      const response = await fetch(`/api/campgrounds?${queryParams}`);
      const data = await response.json();
      
      console.log('Campgrounds API response:', data);
      
      if (data.success) {
        setCampgrounds(data.data || []);
        setTotalPages(data.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching campgrounds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleZipcodeSearch = () => {
    setCurrentPage(1);
    fetchCampgrounds();
  };

  const toggleAmenity = (amenityId) => {
    setSelectedAmenities(prev => 
      prev.includes(amenityId)
        ? prev.filter(id => id !== amenityId)
        : [...prev, amenityId]
    );
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      type: '',
      state: '',
      city: ''
    });
    setZipcode('');
    setRadius(50);
    setSelectedAmenities([]);
    setCurrentPage(1);
  };

  const getAmenityCategoryColor = (category) => {
    switch (category) {
      case 'RV Type': return 'blue';
      case 'Park Amenity': return 'green';
      case 'Utility': return 'orange';
      default: return 'gray';
    }
  };

  const groupedAmenities = amenities.reduce((acc, amenity) => {
    if (!acc[amenity.category]) {
      acc[amenity.category] = [];
    }
    acc[amenity.category].push(amenity);
    return acc;
  }, {});

  return (
    <Container size="xl" py="md">
      <Stack gap="xl">
        <Title order={1} c="orange" ta="center">RV & Camping</Title>
        <Text c="dimmed" ta="center" maw={600} mx="auto">
          Discover amazing RV parks and camping destinations
        </Text>

        {/* Zipcode Location Search */}
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Title order={3}>Find Campgrounds Near You</Title>
            <Grid>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <TextInput
                  label="Zipcode"
                  placeholder="Enter zipcode (e.g., 97439)"
                  leftSection={<IconMapPin size={16} />}
                  value={zipcode}
                  onChange={(e) => setZipcode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleZipcodeSearch()}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <NumberInput
                  label="Radius (miles)"
                  placeholder="50"
                  min={1}
                  max={500}
                  value={radius}
                  onChange={(value) => setRadius(value || 50)}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Button 
                  fullWidth 
                  mt="xl" 
                  color="orange" 
                  onClick={handleZipcodeSearch}
                  leftSection={<IconSearch size={16} />}
                >
                  Search by Location
                </Button>
              </Grid.Col>
            </Grid>
            {zipcode && (
              <Text size="sm" c="dimmed">
                Showing campgrounds within {radius} miles of {zipcode}
              </Text>
            )}
          </Stack>
        </Paper>

        {/* Amenity Tags Filter */}
        {!loadingAmenities && amenities.length > 0 && (
          <Paper shadow="sm" p="lg" radius="md" withBorder>
            <Group mb="md">
              <IconFilter size={20} />
              <Title order={3}>Filter by Amenities</Title>
              {selectedAmenities.length > 0 && (
                <Button variant="light" size="sm" onClick={() => setSelectedAmenities([])}>
                  Clear Amenities
                </Button>
              )}
            </Group>
            <Stack gap="md">
              {Object.entries(groupedAmenities).map(([category, categoryAmenities]) => (
                <Stack key={category} gap="xs">
                  <Text fw={600} size="sm" c="dimmed">{category}</Text>
                  <Group gap="xs" wrap="wrap">
                    {categoryAmenities.map((amenity) => {
                      const isSelected = selectedAmenities.includes(amenity.id);
                      return (
                        <Badge
                          key={amenity.id}
                          size="lg"
                          variant={isSelected ? "filled" : "outline"}
                          color={getAmenityCategoryColor(category)}
                          style={{ cursor: 'pointer' }}
                          onClick={() => toggleAmenity(amenity.id)}
                          rightSection={isSelected ? <IconX size={12} /> : null}
                        >
                          {amenity.name}
                        </Badge>
                      );
                    })}
                  </Group>
                </Stack>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Other Filters */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group mb="md">
            <IconFilter size={20} />
            <Title order={3}>Additional Filters</Title>
            <Button variant="light" size="sm" onClick={clearFilters}>
              Clear All
            </Button>
          </Group>
          
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                label="Search"
                placeholder="Search by name, description, or location..."
                leftSection={<IconSearch size={16} />}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 3 }}>
              <TextInput
                label="State"
                placeholder="e.g., OR, CA, TX"
                value={filters.state}
                onChange={(e) => handleFilterChange('state', e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 3 }}>
              <TextInput
                label="City"
                placeholder="City name"
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
              />
            </Grid.Col>
          </Grid>
        </Card>

        {/* Selected Amenities Display */}
        {selectedAmenities.length > 0 && (
          <Paper p="md" radius="md" withBorder bg="orange.0">
            <Group gap="xs" wrap="wrap">
              <Text size="sm" fw={500}>Selected:</Text>
              {selectedAmenities.map((amenityId) => {
                const amenity = amenities.find(a => a.id === amenityId);
                return amenity ? (
                  <Badge
                    key={amenityId}
                    color={getAmenityCategoryColor(amenity.category)}
                    rightSection={
                      <IconX 
                        size={12} 
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleAmenity(amenityId)}
                      />
                    }
                  >
                    {amenity.name}
                  </Badge>
                ) : null;
              })}
            </Group>
          </Paper>
        )}

        {/* Campgrounds Grid */}
        {loading ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} shadow="sm" padding="lg" radius="md" withBorder>
                <Skeleton height={200} mb="md" />
                <Skeleton height={20} mb="sm" />
                <Skeleton height={16} mb="xs" />
                <Skeleton height={16} width="60%" />
              </Card>
            ))}
          </SimpleGrid>
        ) : campgrounds.length > 0 ? (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
              {campgrounds.map((campground) => (
                <Card 
                  key={campground.id} 
                  shadow="sm" 
                  padding="lg" 
                  radius="md" 
                  withBorder
                  component={Link}
                  to={`/campgrounds/${campground.id}`}
                  style={{ textDecoration: 'none', cursor: 'pointer' }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <Card.Section>
                    {campground.thumbnail ? (
                      <Image
                        src={campground.thumbnail}
                        height={200}
                        alt={campground.name}
                      />
                    ) : (
                      <div style={{ height: 200, backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconTent size={48} color="#6c757d" />
                      </div>
                    )}
                  </Card.Section>

                  <Stack gap="sm" mt="md">
                    <Group justify="space-between">
                      <Badge color="orange" variant="light">
                        {campground.type}
                      </Badge>
                      {campground.featured && (
                        <Badge color="green" variant="light">
                          Featured
                        </Badge>
                      )}
                    </Group>

                    <Title order={4} c="dark" lineClamp={2}>
                      {campground.name}
                    </Title>

                    <Text c="dimmed" size="sm" lineClamp={2}>
                      {campground.description}
                    </Text>

                    <Group gap="xs" c="dimmed">
                      <IconMapPin size={14} />
                      <Text size="sm">
                        {campground.city}, {campground.state}
                      </Text>
                    </Group>

                    {campground.mainPhone && (
                      <Group gap="xs" c="dimmed">
                        <IconPhone size={14} />
                        <Text size="sm">
                          {campground.mainPhone}
                        </Text>
                      </Group>
                    )}

                    {campground.amenities && campground.amenities.length > 0 && (
                      <Group gap="xs" mt="xs" wrap="wrap">
                        {campground.amenities.slice(0, 3).map((amenity) => (
                          <Badge key={amenity.id} size="sm" variant="outline" color="gray">
                            {amenity.name}
                          </Badge>
                        ))}
                        {campground.amenities.length > 3 && (
                          <Badge size="sm" variant="outline" color="gray">
                            +{campground.amenities.length - 3} more
                          </Badge>
                        )}
                      </Group>
                    )}
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>

            {totalPages > 1 && (
              <Group justify="center" mt="xl">
                <Pagination
                  value={currentPage}
                  onChange={setCurrentPage}
                  total={totalPages}
                  color="orange"
                />
              </Group>
            )}
          </>
        ) : (
          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <Stack align="center" gap="md">
              <IconTent size={48} color="dimmed" />
              <Title order={4} c="dimmed">No campgrounds found</Title>
              <Text c="dimmed" ta="center">
                Try adjusting your search criteria or filters
              </Text>
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
};

export default Campgrounds;

